/**
 * Title/Escrow Assignment Modal (Sheet)
 *
 * Multi-step flow for assigning conditions to title/escrow:
 * 1. Select recipient party (Title or Escrow contact)
 * 2. Select conditions to assign
 * 3. Attach documents (from Encompass eFolder or upload)
 * 4. Preview what they'll see
 * 5. Send/Generate the portal link
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Building,
  Copy,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Mail,
  Phone,
  ArrowLeft,
  ArrowRight,
  Send,
  User,
  Check,
  Download,
  Upload,
  FileText,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { EncompassCondition } from '@/types/approval-letter';
import type {
  TitleEscrowInfo,
  ConditionsPortalLoanData,
  ConditionsPortalLoanOfficer,
  ConditionsPortalCompany,
  ConditionTemplateInfo,
  SelectedDocumentForAttachment
} from '@/types/conditions-portal';
import { createComponentLogger } from '@/utils/logger';
import { supabase } from '@/utils/supabase';
import { useEncompassDocuments } from '@/hooks/useEncompassDocuments';
import { PortalHeader } from './PortalHeader';
import { LoanSummaryCard } from './LoanSummaryCard';
import { ContactCards } from './ContactCards';
import { PortalHelpSection } from './PortalHelpSection';
import { PortalFooter } from './PortalFooter';
import { TitleEscrowConditionsSection } from './TitleEscrowConditionsSection';
import { DocumentAttachmentStep } from './DocumentAttachmentStep';

const log = createComponentLogger('TitleEscrowAssignModal', 'conditions-portal');

// API configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;


interface TitleEscrowAssignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conditions: EncompassCondition[];
  token?: string; // Required for external (token-based) access
  titleEscrowContact: TitleEscrowInfo | null;
  onSuccess: (portalUrl: string) => void;
  // New props for professional preview
  loanData: ConditionsPortalLoanData;
  loanOfficer: ConditionsPortalLoanOfficer | null;
  company: ConditionsPortalCompany;
  templates: Record<string, ConditionTemplateInfo>;
  // Additional CC recipients from notification settings
  additionalCcRecipients?: Array<{ id: string; name: string; email: string }>;
  // Loan ID for document picker (Encompass loan ID)
  loanId: string;
  // Internal mode props (JWT-based access)
  isInternal?: boolean;
  jwt?: string;
}

interface AssignmentResult {
  success: boolean;
  portalUrl?: string;
  escrowOfficerEmail?: string | null;
  escrowOfficerName?: string;
  assignedCount?: number;
  error?: string;
}

interface RecipientOption {
  id: string;
  type: 'title' | 'escrow';
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
}

type Step = 'select' | 'documents' | 'preview' | 'success' | 'error';

// Selection data for each condition (includes optional note)
interface ConditionSelection {
  note: string;
}

const FALLBACK_LOAN_OFFICER: ConditionsPortalLoanOfficer = {
  id: '',
  name: 'Loan Officer',
  firstName: '',
  lastName: '',
  email: '',
  phone: null,
  mobilePhone: null,
  nmls: null,
  title: null,
  photoUrl: null
};

export const TitleEscrowAssignModal = ({
  open,
  onOpenChange,
  conditions,
  token,
  titleEscrowContact,
  onSuccess,
  loanData,
  loanOfficer,
  company,
  templates,
  additionalCcRecipients,
  loanId,
  isInternal = false
}: TitleEscrowAssignModalProps) => {
  const displayLoanOfficer = loanOfficer ?? FALLBACK_LOAN_OFFICER;
  const hasRealLoanOfficerEmail =
    !!displayLoanOfficer.email && displayLoanOfficer.email !== 'processing@lendwisemtg.com';

  const [step, setStep] = useState<Step>('select');
  const [selectedRecipient, setSelectedRecipient] = useState<RecipientOption | null>(null);
  // Map of conditionId -> { note: string } for selections with optional notes
  const [selections, setSelections] = useState<Map<string, ConditionSelection>>(new Map());
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<AssignmentResult | null>(null);
  const [ccEmailInput, setCcEmailInput] = useState('');
  // Documents to attach to the portal
  const [selectedDocuments, setSelectedDocuments] = useState<SelectedDocumentForAttachment[]>([]);

  // Preload Encompass documents when modal opens (for faster document step)
  const {
    documents: encompassDocuments,
    loading: documentsLoading,
    error: documentsError
  } = useEncompassDocuments(loanId);

  /**
   * Get fresh JWT for internal API calls
   */
  const getFreshJwt = async (): Promise<string | null> => {
    if (!isInternal) {
      return null;
    }
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData.session?.access_token || null;
  };

  const titleEscrowCompanyName = titleEscrowContact?.companyName;
  const titleEscrowContactName = titleEscrowContact?.contactName;
  const titleEscrowEmail = titleEscrowContact?.email;
  const titleEscrowPhone = titleEscrowContact?.phone;

  // Build recipient options from titleEscrowContact
  const recipientOptions = useMemo<RecipientOption[]>(() => {
    const options: RecipientOption[] = [];

    if (titleEscrowCompanyName || titleEscrowContactName || titleEscrowEmail || titleEscrowPhone) {
      // Add escrow officer as option
      if (titleEscrowContactName || titleEscrowEmail) {
        options.push({
          id: 'escrow',
          type: 'escrow',
          name: titleEscrowContactName || 'Escrow Officer',
          company: titleEscrowCompanyName || null,
          email: titleEscrowEmail || null,
          phone: titleEscrowPhone || null
        });
      }

      // Add title company as separate option if there's company info
      if (titleEscrowCompanyName && !titleEscrowContactName) {
        options.push({
          id: 'title',
          type: 'title',
          name: titleEscrowCompanyName,
          company: null,
          email: titleEscrowEmail || null,
          phone: titleEscrowPhone || null
        });
      }
    }

    // Add a generic option if no contacts available
    if (options.length === 0) {
      options.push({
        id: 'generic',
        type: 'escrow',
        name: 'Title/Escrow Company',
        company: null,
        email: null,
        phone: null
      });
    }

    return options;
  }, [
    titleEscrowCompanyName,
    titleEscrowContactName,
    titleEscrowEmail,
    titleEscrowPhone
  ]);

  // Reset state when modal opens (no auto-selection)
  useEffect(() => {
    if (open) {
      setStep('select');
      setResult(null);
      setSelections(new Map()); // Start with nothing selected
      setOpenNotes(new Set()); // Clear note open states
      setCcEmailInput(''); // Clear CC emails
      setSelectedDocuments([]); // Clear documents

      // Pre-select first recipient only
      setSelectedRecipient(recipientOptions[0] ?? null);
    }
  }, [open, recipientOptions]);

  // Get selected conditions
  const selectedConditions = conditions.filter((c) => {
    const conditionId = c.externalId || c.id || c.internalId;
    return conditionId && selections.has(conditionId);
  });

  /**
   * Toggle condition selection
   */
  const toggleCondition = (conditionId: string) => {
    setSelections((prev) => {
      const next = new Map(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.set(conditionId, { note: '' });
      }
      return next;
    });
  };

  /**
   * Toggle note section open/closed for a condition
   */
  const toggleNoteOpen = (conditionId: string) => {
    setOpenNotes(prev => {
      const next = new Set(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.add(conditionId);
      }
      return next;
    });
  };

  /**
   * Update note for a condition
   */
  const updateNote = (conditionId: string, note: string) => {
    setSelections((prev) => {
      const next = new Map(prev);
      if (next.has(conditionId)) {
        next.set(conditionId, { note: note.substring(0, 250) }); // Limit 250 chars
      }
      return next;
    });
  };

  /**
   * Select/deselect all conditions
   */
  const toggleAll = () => {
    if (selections.size === conditions.length) {
      setSelections(new Map());
      setOpenNotes(new Set()); // Clear open states when deselecting all
    } else {
      const newSelections = new Map<string, ConditionSelection>();
      conditions.forEach((c) => {
        const id = c.externalId || c.id || c.internalId;
        if (id) {
          // Preserve existing notes when selecting all
          const existingNote = selections.get(id)?.note || '';
          newSelections.set(id, { note: existingNote });
        }
      });
      setSelections(newSelections);
    }
  };

  /**
   * Go to documents step (from select)
   */
  const handleGoToDocuments = () => {
    if (selections.size > 0) {
      setStep('documents');
    }
  };

  /**
   * Go to preview step (from documents)
   */
  const handleGoToPreview = () => {
    setStep('preview');
  };

  /**
   * Go back from documents to select
   */
  const handleBackToSelect = () => {
    setStep('select');
  };

  /**
   * Go back from preview to documents
   */
  const handleBackToDocuments = () => {
    setStep('documents');
  };

  /**
   * Submit assignment to API
   */
  const handleSend = async () => {
    if (selections.size === 0) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Build conditions array with notes
      const conditionsWithNotes = Array.from(selections.entries()).map(([conditionId, data]) => ({
        conditionId,
        note: data.note.trim() || undefined // Only include if not empty
      }));

      // Build documents array for API
      const documentsForApi = selectedDocuments.map(doc => ({
        source: doc.source,
        attachmentId: doc.attachmentId,
        displayName: doc.displayName,
        storagePath: doc.storagePath,
        fileSize: doc.fileSize,
        mimeType: doc.mimeType,
        originalFilename: doc.source === 'upload' ? doc.displayName : undefined
      }));

      log.info('Submitting title/escrow assignment', {
        count: conditionsWithNotes.length,
        recipient: selectedRecipient?.name,
        withNotes: conditionsWithNotes.filter(c => c.note).length,
        documentCount: documentsForApi.length
      });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY
      };

      // Add JWT auth header for internal mode (get fresh token)
      if (isInternal) {
        const freshJwt = await getFreshJwt();
        if (!freshJwt) {
          throw new Error('Session expired. Please refresh the page.');
        }
        headers['Authorization'] = `Bearer ${freshJwt}`;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: isInternal ? 'assign-title-escrow-internal' : 'assign-title-escrow',
          ...(isInternal ? { loanId } : { token }),
          conditions: conditionsWithNotes, // New format with notes
          documents: documentsForApi, // Attached documents
          ccEmails: ccEmailInput.trim()
            ? ccEmailInput.split(',').map(e => e.trim()).filter(e => e.includes('@'))
            : []
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to assign conditions');
      }

      log.info('Title/Escrow portal created', {
        assignedCount: data.assignedCount,
        escrowOfficer: data.escrowOfficerName
      });

      setResult({
        success: true,
        portalUrl: data.portalUrl,
        escrowOfficerEmail: data.escrowOfficerEmail || selectedRecipient?.email,
        escrowOfficerName: data.escrowOfficerName || selectedRecipient?.name,
        assignedCount: data.assignedCount
      });

      setStep('success');
      onSuccess(data.portalUrl);
    } catch (error) {
      log.error('Title/Escrow assignment failed', { error });
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to assign conditions'
      });
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Copy portal URL to clipboard
   */
  const copyToClipboard = async () => {
    if (result?.portalUrl) {
      await navigator.clipboard.writeText(result.portalUrl);
    }
  };

  /**
   * Open portal URL in new tab
   */
  const openPortal = () => {
    if (result?.portalUrl) {
      window.open(result.portalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Reset and close modal
   */
  const handleClose = () => {
    setStep('select');
    setResult(null);
    setSelectedRecipient(recipientOptions[0] || null);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="!w-[80vw] !max-w-none p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-[#183C2E]" />
            <SheetTitle>
              {step === 'select' && 'Route to Title & Escrow'}
              {step === 'documents' && 'Attach Documents'}
              {step === 'preview' && 'Preview Portal'}
              {step === 'success' && 'Portal Created'}
              {step === 'error' && 'Error'}
            </SheetTitle>
          </div>
          <SheetDescription>
            {step === 'select' && 'Select conditions to route to the settlement agent.'}
            {step === 'documents' && 'Attach reference documents for the settlement agent.'}
            {step === 'preview' && 'Review how the settlement agent will see these conditions.'}
            {step === 'success' && 'The portal link has been created successfully.'}
            {step === 'error' && 'There was a problem creating the portal.'}
          </SheetDescription>
        </SheetHeader>

        {/* STEP: SELECT */}
        {step === 'select' && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4 space-y-6">
                {/* Recipient Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Select Recipient</label>
                  <div className="grid gap-2">
                    {recipientOptions.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => setSelectedRecipient(option)}
                        className={cn(
                          'flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors border',
                          selectedRecipient?.id === option.id
                            ? 'bg-[#183C2E]/5 border-2 border-[#183C2E]/30'
                            : 'hover:bg-muted border-border'
                        )}
                      >
                        <div className={cn(
                          'h-5 w-5 rounded-full border-2 flex items-center justify-center mt-0.5',
                          selectedRecipient?.id === option.id
                            ? 'border-[#183C2E] bg-[#183C2E]'
                            : 'border-muted-foreground'
                        )}>
                          {selectedRecipient?.id === option.id && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{option.name}</span>
                            <span className="text-xs bg-muted px-2 py-0.5 rounded capitalize">
                              {option.type}
                            </span>
                          </div>
                          {option.company && (
                            <p className="text-sm text-muted-foreground mt-1 pl-6">
                              {option.company}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 mt-2 pl-6">
                            {option.email && (
                              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                <Mail className="h-3.5 w-3.5" />
                                {option.email}
                              </div>
                            )}
                            {option.phone && (
                              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                <Phone className="h-3.5 w-3.5" />
                                {option.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CC Email Input */}
                  <div className="mt-4 space-y-3">
                    <label className="text-sm text-muted-foreground">
                      CC Recipients
                    </label>

                    {/* Auto-CC Recipients Display */}
                    <div className="space-y-1.5">
                      <span className="text-xs text-muted-foreground">Auto CC:</span>
                      <div className="flex flex-wrap gap-2">
                        {/* Only show processor if it's a real person (not fallback) */}
                        {loanData.processor?.email && loanData.processor.email !== 'processing@lendwisemtg.com' && (
                          <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-xs">
                            <Mail className="h-3 w-3" />
                            <span className="font-medium">{loanData.processor.name}</span>
                            <span className="text-blue-500">({loanData.processor.email})</span>
                          </div>
                        )}
                        {/* Only show LO if it's a real person (not fallback) */}
                        {hasRealLoanOfficerEmail && (
                          <div className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 px-2.5 py-1 rounded-md text-xs">
                            <Mail className="h-3 w-3" />
                            <span className="font-medium">{displayLoanOfficer.name}</span>
                            <span className="text-purple-500">({displayLoanOfficer.email})</span>
                          </div>
                        )}
                        {/* Additional CC recipients from notification settings */}
                        {additionalCcRecipients?.map((recipient) => (
                          <div key={recipient.id} className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-md text-xs">
                            <Mail className="h-3 w-3" />
                            <span className="font-medium">{recipient.name}</span>
                            <span className="text-green-500">({recipient.email})</span>
                          </div>
                        ))}
                        {/* Show message only if no real recipients (excluding fallbacks) */}
                        {(!loanData.processor?.email || loanData.processor.email === 'processing@lendwisemtg.com') &&
                         !hasRealLoanOfficerEmail &&
                         (!additionalCcRecipients || additionalCcRecipients.length === 0) && (
                          <span className="text-xs text-muted-foreground italic">No auto-CC recipients configured</span>
                        )}
                      </div>
                    </div>

                    {/* Additional CC Input */}
                    <div className="space-y-1.5">
                      <span className="text-xs text-muted-foreground">Additional CC (optional):</span>
                      <Input
                        placeholder="email@example.com, another@example.com"
                        value={ccEmailInput}
                        onChange={(e) => setCcEmailInput(e.target.value)}
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Separate multiple emails with commas
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Conditions Selection */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Select Conditions ({selections.size} of {conditions.length})
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleAll}
                      className="text-xs h-7"
                    >
                      {selections.size === conditions.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {conditions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No open conditions available to assign.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {conditions.map((condition) => {
                        const conditionId = condition.externalId || condition.id || condition.internalId;
                        const isSelected = conditionId ? selections.has(conditionId) : false;
                        const currentNote = conditionId ? selections.get(conditionId)?.note || '' : '';
                        const description = condition.externalDescription || condition.internalDescription;

                        return (
                          <div
                            key={conditionId}
                            className={cn(
                              'p-4 rounded-lg transition-colors',
                              isSelected
                                ? 'bg-[#183C2E]/5 border-2 border-[#183C2E]/30'
                                : 'hover:bg-muted border border-border'
                            )}
                          >
                            <div
                              className="flex items-start gap-3 cursor-pointer"
                              onClick={() => conditionId && toggleCondition(conditionId)}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => conditionId && toggleCondition(conditionId)}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-1"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium leading-tight">
                                  {condition.title || 'Untitled Condition'}
                                </p>
                                {description && (
                                  <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                                    {description}
                                  </p>
                                )}
                                <div className="flex flex-wrap items-center gap-2 pt-2">
                                  <span
                                    className={cn(
                                      'text-xs font-medium px-2 py-0.5 rounded',
                                      condition.priorTo === 'Docs'
                                        ? 'bg-blue-100 text-blue-700'
                                        : condition.priorTo === 'Funding'
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-purple-100 text-purple-700'
                                    )}
                                  >
                                    {condition.priorTo === 'Docs'
                                      ? 'Prior to Docs'
                                      : condition.priorTo === 'Funding'
                                        ? 'Prior to Funding'
                                        : 'At Closing'}
                                  </span>
                                  {condition.category && (
                                    <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">
                                      {condition.category}
                                    </span>
                                  )}
                                </div>

                                {/* Collapsible Note Ribbon - only shows when selected */}
                                {isSelected && conditionId && (
                                  <Collapsible
                                    open={openNotes.has(conditionId)}
                                    onOpenChange={() => toggleNoteOpen(conditionId)}
                                  >
                                    <div className="border-t border-border/50 mt-3 pt-2">
                                      <CollapsibleTrigger
                                        className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <span className="flex items-center gap-1.5">
                                          <ChevronDown className={cn(
                                            'h-4 w-4 transition-transform duration-200',
                                            openNotes.has(conditionId) && 'rotate-180'
                                          )} />
                                          {currentNote ? 'Edit Note' : 'Add Note'}
                                        </span>
                                        {currentNote && (
                                          <span className="text-xs">
                                            {currentNote.length}/250
                                          </span>
                                        )}
                                      </CollapsibleTrigger>
                                      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                                        <Textarea
                                          placeholder="Add notes for the recipient..."
                                          value={currentNote}
                                          onChange={(e) => updateNote(conditionId, e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          className="mt-2 min-h-[60px] max-h-[100px] text-sm resize-none"
                                          maxLength={250}
                                        />
                                        <div className="text-xs text-muted-foreground text-right mt-1">
                                          {currentNote.length}/250
                                        </div>
                                      </CollapsibleContent>
                                    </div>
                                  </Collapsible>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleGoToDocuments}
                disabled={selections.size === 0}
                className="flex-1 bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E]"
              >
                <FileText className="h-4 w-4 mr-2" />
                Attach Documents
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP: DOCUMENTS - Attach documents from Encompass or upload */}
        {step === 'documents' && (
          <DocumentAttachmentStep
            loanId={loanId}
            selectedDocuments={selectedDocuments}
            onDocumentsChange={setSelectedDocuments}
            onBack={handleBackToSelect}
            onNext={handleGoToPreview}
            encompassDocuments={encompassDocuments}
            documentsLoading={documentsLoading}
            documentsError={documentsError}
          />
        )}

        {/* STEP: PREVIEW - Professional Portal Layout */}
        {step === 'preview' && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="space-y-0">
                {/* Recipient Info Banner */}
                <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-800">
                      <strong>Sending to:</strong> {selectedRecipient?.name}
                      {selectedRecipient?.email && ` (${selectedRecipient.email})`}
                    </span>
                  </div>
                </div>

                {/* Professional Portal Preview */}
                <div className="bg-gray-50">
                  {/* Portal Header */}
                  <PortalHeader company={company} />

                  {/* Main Content */}
                  <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
                    {/* Loan Summary with Urgency */}
                    <LoanSummaryCard loanData={loanData} />

                    {/* Contact Cards */}
                    <ContactCards loanOfficer={displayLoanOfficer} processor={loanData.processor} />

                    {/* Conditions Section */}
                    <div className="bg-white rounded-lg border shadow-sm">
                      <div className="px-4 py-3 border-b">
                        <h3 className="font-semibold flex items-center gap-2">
                          <FileText className="h-5 w-5 text-[#183C2E]" />
                          Assigned Conditions ({selectedConditions.length})
                        </h3>
                      </div>
                      <div className="p-4 space-y-6">
                        {/* Build notes map for preview - keyed by conditionId for ConditionsSection lookup */}
                        {(() => {
                          const previewNotes: Record<string, string> = {};
                          selectedConditions.forEach((condition) => {
                            const conditionId = condition.externalId || condition.id || condition.internalId;
                            const note = conditionId ? selections.get(conditionId)?.note : undefined;
                            if (note && conditionId) {
                              previewNotes[conditionId] = note;
                            }
                          });

                          return (
                            <>
                              {/* PTD - Prior to Docs */}
                              {(() => {
                                const ptdConditions = selectedConditions.filter(c => c.priorTo === 'Docs');
                                return ptdConditions.length > 0 ? (
                                  <TitleEscrowConditionsSection
                                    title="Prior to Docs"
                                    conditions={ptdConditions}
                                    templates={templates}
                                    uploads={[]}
                                    notes={previewNotes}
                                    onDownloadTemplate={() => {}}
                                    onUploadClick={() => {}}
                                    borrowerPairs={loanData.borrowerPairs}
                                  />
                                ) : null;
                              })()}

                              {/* PTF - Prior to Funding */}
                              {(() => {
                                const ptfConditions = selectedConditions.filter(c => c.priorTo === 'Funding');
                                return ptfConditions.length > 0 ? (
                                  <TitleEscrowConditionsSection
                                    title="Prior to Funding"
                                    conditions={ptfConditions}
                                    templates={templates}
                                    uploads={[]}
                                    notes={previewNotes}
                                    onDownloadTemplate={() => {}}
                                    onUploadClick={() => {}}
                                    borrowerPairs={loanData.borrowerPairs}
                                  />
                                ) : null;
                              })()}

                              {/* AC - At Closing */}
                              {(() => {
                                const acConditions = selectedConditions.filter(c => c.priorTo === 'Closing');
                                return acConditions.length > 0 ? (
                                  <TitleEscrowConditionsSection
                                    title="At Closing"
                                    conditions={acConditions}
                                    templates={templates}
                                    uploads={[]}
                                    notes={previewNotes}
                                    onDownloadTemplate={() => {}}
                                    onUploadClick={() => {}}
                                    borrowerPairs={loanData.borrowerPairs}
                                  />
                                ) : null;
                              })()}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Attached Documents Section (Preview) */}
                    {selectedDocuments.length > 0 && (
                      <div className="bg-white rounded-lg border shadow-sm">
                        <div className="px-4 py-3 border-b">
                          <h3 className="font-semibold flex items-center gap-2">
                            <FileText className="h-5 w-5 text-[#183C2E]" />
                            Reference Documents ({selectedDocuments.length})
                          </h3>
                        </div>
                        <div className="p-4">
                          <p className="text-sm text-muted-foreground mb-3">
                            These documents will be available for download in the portal.
                          </p>
                          <div className="space-y-2">
                            {selectedDocuments.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                              >
                                {doc.source === 'encompass' ? (
                                  <Download className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                ) : (
                                  <Upload className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{doc.displayName}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {doc.source === 'encompass' ? 'From Encompass' : 'Uploaded'}
                                    {doc.fileSize && ` • ${(doc.fileSize / 1024).toFixed(0)} KB`}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Help Section */}
                    <PortalHelpSection />
                  </div>

                  {/* Portal Footer */}
                  <PortalFooter company={company} />
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleBackToDocuments} className="flex-1">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSend}
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Portal...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Portal Link
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP: SUCCESS */}
        {step === 'success' && result?.portalUrl && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">
                      Portal Link Created Successfully
                    </p>
                    <p className="text-sm text-green-700">
                      {result.assignedCount} condition{result.assignedCount !== 1 ? 's' : ''} assigned
                      {result.escrowOfficerName && ` to ${result.escrowOfficerName}`}
                    </p>
                  </div>
                </div>

                {/* Portal URL */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Portal Link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={result.portalUrl}
                      className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted font-mono truncate"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyToClipboard}
                      title="Copy link"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={openPortal}
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Email Link */}
                {result.escrowOfficerEmail && (
                  <div className="p-4 bg-muted rounded-lg space-y-3">
                    <p className="text-sm font-medium">Send via Email</p>
                    <a
                      href={`mailto:${result.escrowOfficerEmail}?subject=Conditions%20Portal%20Access&body=Please%20use%20this%20link%20to%20view%20and%20upload%20documents%20for%20your%20assigned%20conditions:%0A%0A${encodeURIComponent(result.portalUrl)}`}
                      className="inline-flex items-center gap-2 text-sm text-[#183C2E] hover:underline"
                    >
                      <Mail className="h-4 w-4" />
                      Send to {result.escrowOfficerEmail}
                    </a>
                  </div>
                )}

                {/* Notification Info */}
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> An email notification has been sent to the loan team
                    (Loan Officer, Processor, LO2, and Loan Officer Assistant) about this assignment.
                  </p>
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background">
              <Button onClick={handleClose} className="w-full">Done</Button>
            </div>
          </div>
        )}

        {/* STEP: ERROR */}
        {step === 'error' && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">
                      Failed to Create Portal
                    </p>
                    <p className="text-sm text-red-700">{result?.error}</p>
                  </div>
                </div>
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleGoToPreview} className="flex-1">
                Try Again
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default TitleEscrowAssignModal;
